'use client';

import React from 'react';
import { MaturityData, MaturityKey } from '../data/initialData';

interface MaturitySelectorProps {
  maturities: MaturityData[];
  selectedMaturity: MaturityKey;
  onSelect: (m: MaturityKey) => void;
}

const formatLiquidity = (v: number) => {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v}`;
};

export default function MaturitySelector({
  maturities,
  selectedMaturity,
  onSelect,
}: MaturitySelectorProps) {
  return (
    <div className="spine-card p-4">
      <div className="flex items-center gap-2 mb-3">
        <p className="section-label">Select Maturity</p>
        <span className="text-xs text-gray-400">— click to explore creditbook depth</span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {maturities.map((m) => {
          const isSelected = m.maturity === selectedMaturity;
          const utilization = m.totalLiquidity > 0
            ? (m.totalBorrowDemand / m.totalLiquidity) * 100
            : 0;
          const isHighUtil = utilization > 85;

          return (
            <button
              key={m.maturity}
              onClick={() => onSelect(m.maturity)}
              className={`relative flex flex-col items-center gap-1 rounded-xl px-3 py-3 transition-all duration-200 border select-none active:scale-95 ${
                isSelected
                  ? 'bg-[#6C47FF] border-[#6C47FF] shadow-violet text-white'
                  : 'bg-white border-gray-200 hover:border-[#6C47FF]/40 hover:bg-[#F5F3FF] text-gray-700'
              }`}
            >
              {/* High utilization warning dot */}
              {isHighUtil && !isSelected && (
                <span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-amber-500" title="High utilization" />
              )}
              {isHighUtil && isSelected && (
                <span className="absolute top-2 right-2 w-1.5 h-1.5 rounded-full bg-amber-300" />
              )}

              <span className={`text-sm font-bold ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                {m.maturity}
              </span>
              <span className={`text-xs font-mono font-semibold ${isSelected ? 'text-violet-200' : 'text-[#6C47FF]'}`}>
                {m.apr.toFixed(2)}%
              </span>
              <span className={`text-xs font-medium ${isSelected ? 'text-violet-300' : 'text-gray-400'}`}>
                {formatLiquidity(m.totalLiquidity)}
              </span>

              {/* Utilization bar */}
              <div className={`w-full h-1 rounded-full mt-1 ${isSelected ? 'bg-white/20' : 'bg-gray-100'}`}>
                <div
                  className={`h-full rounded-full transition-all duration-300 ${
                    isSelected
                      ? utilization > 85 ? 'bg-amber-300' : 'bg-white/60'
                      : utilization > 85 ? 'bg-amber-400' : 'bg-[#6C47FF]/60'
                  }`}
                  style={{ width: `${Math.min(100, utilization)}%` }}
                />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}